/**
 * The classic Ultima Online viewport cursor (issue #51): an armoured gauntlet
 * pointing a finger, shown while the pointer is over the game viewport in place
 * of the browser's default arrow. While the pointer hovers over the game world,
 * the gauntlet swaps to the matching **directional** art so it previews the
 * direction a right-click would move; idle gauntlet in the turn-only zone.
 *
 * It is a pure CSS cursor, applied to the `#app` host that owns the Pixi canvas.
 * That is deliberately the lightest mechanism that meets every requirement:
 *
 * - **Distinct over standard UI.** The splash overlay and chat box are siblings of
 *   `#app`, so the viewport cursor remains scoped there. Clickable DOM controls use
 *   the north-pointing gauntlet published by {@link applyUiCursor}; text, drag,
 *   resize, disabled, and targeting controls retain their specialized cursors.
 * - **Hotspot-aligned.** The CSS `url(...) x y` syntax places the cursor's active
 *   click point at the gauntlet's fingertip ({@link GAUNTLET_HOTSPOT}), so a click
 *   registers exactly under the finger, not the image's top-left corner.
 * - **No interaction interference.** A CSS cursor is purely visual: it never
 *   captures pointer events, so door clicks (and the right-click-to-walk of #52)
 *   are untouched. Pixi's default cursor mode is `inherit`, so the gauntlet shows
 *   across the whole viewport; door sprites opt into `inherit` too (rather than a
 *   system `pointer`) so the gauntlet stays put even over a clickable door.
 * - **Directional with no input lag.** {@link setDirectionalCursor} only rewrites
 *   `style.cursor` when the hovered heading changes, so the OS cursor stays
 *   hardware-accelerated with no per-frame churn. Keyboard and route movement do
 *   not rotate it. All nine WebPs are {@link preloadGameCursors preloaded} at boot
 *   so the first show of each direction does not flash.
 *
 * The art is the **pipeline-baked** UO gauntlet cursor: `npm run convert` decodes
 * the real client static art (the ids declared in `cursors` in `src/sim/content.ts`)
 * into `assets/baked/cursor/<id>.webp`, exactly like every other generated UO
 * asset. On a clientless host (CI/dev) `stub-assets` writes a hand-drawn
 * **recreation** to those same paths instead, so a gauntlet always renders - the
 * art degrades to a placeholder, the wiring never changes. The captain confirms
 * the genuine cursors (and the directional ids/hotspots) by running the bake. See
 * ADR-0021 and ADR-0035.
 */
import type { Direction } from '../sim/direction';
import { cursors, scaledHotspot, type CursorDefinition } from '../sim/content';
import gauntletUrl from '../../assets/baked/cursor/gauntlet.webp?url';
import gauntletNUrl from '../../assets/baked/cursor/gauntlet-n.webp?url';
import gauntletNeUrl from '../../assets/baked/cursor/gauntlet-ne.webp?url';
import gauntletEUrl from '../../assets/baked/cursor/gauntlet-e.webp?url';
import gauntletSeUrl from '../../assets/baked/cursor/gauntlet-se.webp?url';
import gauntletSUrl from '../../assets/baked/cursor/gauntlet-s.webp?url';
import gauntletSwUrl from '../../assets/baked/cursor/gauntlet-sw.webp?url';
import gauntletWUrl from '../../assets/baked/cursor/gauntlet-w.webp?url';
import gauntletNwUrl from '../../assets/baked/cursor/gauntlet-nw.webp?url';
import duelTargetUrl from '../../assets/baked/cursor/duel-target.webp?url';

/**
 * The baked WebP URL for each cursor id, keyed by content id. Static `?url`
 * imports (Vite needs them literal) mirror the `cursors` registry one-to-one, so
 * a missing entry fails the build rather than silently dropping a direction.
 */
const URL_BY_ID: Readonly<Record<string, string>> = {
  gauntlet: gauntletUrl,
  'gauntlet-n': gauntletNUrl,
  'gauntlet-ne': gauntletNeUrl,
  'gauntlet-e': gauntletEUrl,
  'gauntlet-se': gauntletSeUrl,
  'gauntlet-s': gauntletSUrl,
  'gauntlet-sw': gauntletSwUrl,
  'gauntlet-w': gauntletWUrl,
  'gauntlet-nw': gauntletNwUrl,
};

/** Build the CSS `cursor` value for one cursor def: its url anchored at the scaled fingertip hotspot, system arrow (`auto`) as the fetch-failure fallback. */
function cursorCss(
  def: CursorDefinition,
  url: string = URL_BY_ID[def.id]!,
  fallback: 'auto' | 'pointer' = 'auto',
): string {
  const h = scaledHotspot(def.hotspot);
  return `url("${url}") ${h.x} ${h.y}, ${fallback}`;
}

/**
 * The gauntlet's fingertip, in pixels within the baked cursor image - the active
 * "click point" the browser aligns under the mouse position. Derived from the
 * idle gauntlet's native hotspot in the content registry, scaled by `CURSOR_SCALE`
 * (the single source `tools/gen-cursor.ts`'s `HOTSPOT` reads too, so they never
 * drift). Native `(3,1)` -> `(4,1)` at 1.3125x.
 *
 * VERIFY: this is tuned to the recreation's fingertip. The real baked cursors are
 * similarly-sized pointing gauntlets, but their exact fingertip pixel (and each
 * direction's) can only be confirmed against the client - the captain nudges the
 * registry hotspots if the genuine art's fingertips sit elsewhere (see ADR-0035).
 */
export const GAUNTLET_HOTSPOT = scaledHotspot(cursors.get('gauntlet').hotspot);

/**
 * The idle gauntlet CSS `cursor` value, anchored at its fingertip hotspot. `url`
 * is injectable so the value is unit-testable without the asset pipeline.
 */
export function gauntletCursorCss(url: string = gauntletUrl): string {
  return cursorCss(cursors.get('gauntlet'), url);
}

/**
 * The dedicated north-pointing gauntlet used by clickable DOM controls. This is
 * intentionally distinct from the idle viewport gauntlet, whose diagonal pose
 * communicates ordinary movement rather than an actionable control.
 */
export function upGauntletCursorCss(url: string = gauntletNUrl): string {
  return cursorCss(cursors.get('gauntlet-n'), url, 'pointer');
}

/**
 * The duel target cursor CSS value (Player duels, PRD #252, ADR-0139): UO cursor
 * art `0x2076` - the gauntlet-holding-a-targeting-circle - anchored at its reticle
 * hotspot. It is a **fixed, non-directional** targeting graphic: while the duel
 * cursor is armed the whole 'duel' treatment paints this one cursor regardless of
 * heading (a UO target cursor does not rotate with movement), replacing the old
 * blue-tinted gauntlet (ADR-0130). `url` is injectable so the value is
 * unit-testable without the asset pipeline.
 */
export function duelTargetCursorCss(url: string = duelTargetUrl): string {
  return cursorCss(cursors.get('duel-target'), url);
}

/**
 * The CSS `cursor` value per movement {@link Direction}, precomputed once from the
 * registry so a heading change is an O(1) string swap. Only the directional
 * cursors (`dir !== null`) are included; the idle gauntlet is {@link
 * gauntletCursorCss}.
 */
const CSS_BY_DIR: ReadonlyMap<Direction, string> = new Map(
  cursors
    .all()
    .filter((def): def is CursorDefinition & { dir: Direction } => def.dir !== null)
    .map((def) => [def.dir, cursorCss(def)] as const),
);

/** The slice of the host element this touches: just a writable `style.cursor`. */
interface CursorHost {
  style: { cursor: string };
}

/** The document-root style surface used to publish the shared DOM click cursor. */
interface UiCursorHost {
  style: { setProperty(name: string, value: string): void };
}

/**
 * The slice of the Pixi application this touches: the event system's cursor map.
 * Typed as the same open record Pixi exposes (`cursorStyles` is keyed by cursor
 * mode), so a real `Application` is assignable without a cast.
 */
interface CursorApp {
  renderer: { events: { cursorStyles: Record<string, unknown> } };
}

/**
 * Paint the classic UO gauntlet over the viewport by setting the `#app` host's CSS
 * cursor. When the Pixi `app` is passed, its default cursor mode is pinned to
 * `inherit` (already Pixi's default, set explicitly so a future Pixi change can't
 * silently revert the canvas to the system arrow on hover-out): with the canvas
 * inheriting the host, the gauntlet shows across the whole viewport - including
 * over clickable doors, which opt into `inherit` rather than a system `pointer`.
 */
export function applyGameCursor(host: CursorHost, app?: CursorApp): void {
  host.style.cursor = gauntletCursorCss();
  if (app) app.renderer.events.cursorStyles.default = 'inherit';
}

/**
 * Publish the north-pointing gauntlet as the shared DOM click cursor, and the UO
 * duel-targeting gauntlet (`0x2076`, see {@link duelTargetCursorCss}) as the shared
 * DOM *targeting* cursor. The page's clickable UI rules consume the pointer
 * property instead of the native pointer, keeping toolbar buttons, tabs, gump
 * controls, and dynamically-created controls consistent; target-pick states (the
 * card-duel gump's targetable heroes and minions) consume the targeting property
 * instead of a generic crosshair. Drag, resize, disabled, and text cursors remain
 * explicit and unaffected. The Vite asset imports must be supplied from JavaScript
 * because static CSS cannot name their hashed production URLs.
 */
export function applyUiCursor(host: UiCursorHost): void {
  host.style.setProperty('--ui-pointer-cursor', upGauntletCursorCss());
  host.style.setProperty('--ui-target-cursor', duelTargetCursorCss());
}

/**
 * War-mode cursor treatment (Rat Swarm Slice E, ADR-0120): the same gauntlet
 * art washed **blood red**, so entering war mode is legible at a glance (the
 * classic UO cue - the paperdoll war button and aggression UI read red) while
 * every hotspot and the directional-swap machinery stay identical.
 *
 * It stays a pure CSS cursor: {@link prepareWarCursors} bakes each gauntlet PNG
 * once onto an offscreen canvas, floods it with {@link WAR_TINT_CSS}
 * (`source-atop`, so only the gauntlet's own pixels tint), and registers the
 * `toDataURL` result as that cursor id's war variant. Selection then remains an
 * O(1) string swap per heading change - no per-frame work, no filter, no extra
 * DOM. Until a variant is baked (or where canvas is unavailable - headless
 * node), war mode falls back to the plain art rather than a blank cursor.
 */
const WAR_TINT_CSS = 'rgba(200, 16, 16, 0.55)';

/**
 * Which targeting treatment a cursor draws in: the plain gauntlet (`'peace'`), the
 * red war-mode gauntlet (`'war'`), or the fixed duel target cursor (`'duel'`, UO
 * art `0x2076` - see {@link duelTargetCursorCss}). War is a per-direction tinted
 * variant; duel is a single non-directional graphic.
 */
export type CursorMode = 'peace' | 'war' | 'duel';

/** The baked war-variant CSS per cursor id, filled by {@link prepareWarCursors}. */
const WAR_CSS_BY_ID = new Map<string, string>();

/** The cursor id per movement direction, for the tinted-variant lookup. */
const ID_BY_DIR: ReadonlyMap<Direction, string> = new Map(
  cursors
    .all()
    .filter((def): def is CursorDefinition & { dir: Direction } => def.dir !== null)
    .map((def) => [def.dir, def.id] as const),
);

/**
 * Register a baked war-mode cursor variant for a cursor id. Called by
 * {@link prepareWarCursors} as each tinted data-URL finishes; exported so
 * the headless tests can exercise the selection logic without a canvas.
 */
export function registerWarCursorCss(id: string, css: string): void {
  WAR_CSS_BY_ID.set(id, css);
}

/**
 * Drop every registered war-mode variant, restoring the plain-art fallback for
 * all ids. Exported so headless tests can reset the shared module registry
 * between cases - without it a test that registers a variant leaks into a
 * sibling that asserts the pre-bake fallback, making the outcome order-dependent.
 */
export function clearWarCursorCss(): void {
  WAR_CSS_BY_ID.clear();
}

/** The CSS cursor value for a heading (or idle), in the given treatment (with the plain-art fallback). */
function directionalCursorCss(dir: Direction | null, mode: CursorMode): string {
  // The duel target cursor is a single fixed graphic (UO 0x2076), not a per-heading
  // tint - it paints whole for every heading while armed (ADR-0139).
  if (mode === 'duel') return duelTargetCursorCss();
  const peace = dir === null ? gauntletCursorCss() : (CSS_BY_DIR.get(dir) ?? gauntletCursorCss());
  if (mode === 'peace') return peace;
  const id = dir === null ? 'gauntlet' : ID_BY_DIR.get(dir);
  return (id && WAR_CSS_BY_ID.get(id)) || peace;
}

/**
 * Show the gauntlet pointing the given 8-way movement preview, or the idle
 * gauntlet when `dir === null` (unknown hover / turn-only zone). Each
 * direction carries its own fingertip hotspot. `mode` selects the treatment: the
 * plain gauntlet (`'peace'`), the red war-mode variant (`'war'`), or the fixed duel
 * target cursor (`'duel'`, UO `0x2076`) - see {@link duelTargetCursorCss}. Callers
 * gate this to the lightweight change-only path (only on a hovered heading change
 * or a mode toggle) so the OS cursor stays hardware-accelerated and pointer input
 * never lags. See `main.ts`.
 */
export function setDirectionalCursor(
  host: CursorHost,
  dir: Direction | null,
  mode: CursorMode = 'peace',
): void {
  host.style.cursor = directionalCursorCss(dir, mode);
}

/**
 * Bake the red war-mode variants of all nine gauntlets into data-URL cursors
 * (ADR-0120). DOM-only and async - call once from `main.ts` at boot, alongside
 * {@link preloadGameCursors}, so the variants are ready long before the first Tab
 * press; any failure (no canvas, decode error) simply leaves the plain-art fallback
 * in place. The duel target cursor is a distinct baked asset (UO `0x2076`), not a
 * tint, so it needs no bake here - see {@link duelTargetCursorCss}.
 */
export function prepareWarCursors(): void {
  for (const def of cursors.all()) {
    const url = URL_BY_ID[def.id];
    if (!url) continue;
    if (WAR_CSS_BY_ID.has(def.id)) continue;
    const img = new Image();
    img.onload = () => {
      const css = tintedCursorCss(def, img, WAR_TINT_CSS);
      if (css) registerWarCursorCss(def.id, css);
    };
    img.src = url;
  }
}

/**
 * Bake one tinted cursor variant: draw the gauntlet PNG onto an offscreen canvas,
 * flood its own pixels with `tint` (`source-atop`, so transparency stays intact),
 * and return the `cursorCss` for the resulting data-URL. Returns `undefined` where
 * canvas is unavailable/tainted (headless node), so the caller keeps the plain-art
 * fallback.
 */
function tintedCursorCss(def: CursorDefinition, img: HTMLImageElement, tint: string): string | undefined {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.drawImage(img, 0, 0);
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return cursorCss(def, canvas.toDataURL('image/png'));
  } catch {
    return undefined;
  }
}

/**
 * Warm the browser's image cache with all nine cursor PNGs at boot, so the first
 * swap to any direction is an instant style write with no fetch flash. Pure
 * side-effect (decodes into cache); the returned `<img>` handles can be ignored.
 * DOM-only - call once from `main.ts`, never from the pure unit tests.
 */
export function preloadGameCursors(): void {
  for (const url of [...Object.values(URL_BY_ID), duelTargetUrl]) {
    const img = new Image();
    img.src = url;
  }
}
